/**
 * WordPress dependencies
 */
import domReady from '@safe-wordpress/dom-ready';
import { BaseControl, Dashicon } from '@safe-wordpress/components';
import { useDispatch, useSelect } from '@safe-wordpress/data';
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import clsx from 'clsx';
import { last } from 'lodash';
import { Editor } from 'primereact/editor';
import { store as NAB_DATA } from '@nab/data';
import type { CssWorkingContentValue, Maybe } from '@nab/types';

type ElementEditorProps = {
	readonly selector: string;
};

export const ElementEditor = ( {
	selector,
}: ElementEditorProps ): JSX.Element | null => {
	const value = useValue( selector );
	const formats = useFormats( value );
	const setHtml = useHtmlSetter( value );

	if ( ! value ) {
		return null;
	}

	const customHeader = (
		<span
			className={ `${
				formats.length ? 'ql-formats' : 'ql-formats ql-formats--empty'
			}` }
		>
			{ formats.map( ( f ) => (
				<button key={ f } className={ `ql-${ f }` } />
			) ) }
		</span>
	);

	const classes: Record< string, boolean > = {
		[ `nab-ql-editor--has-${ getTagName( value.selector ) }-list` ]: [
			'ol',
			'ul',
		].includes( getTagName( value.selector ) ),
		...FORMATS.filter( ( f ) => ! formats.includes( f ) ).reduce(
			( r, f ) => ( { ...r, [ `nab-ql-editor--is-${ f }` ]: true } ),
			{} as Record< string, true >
		),
	};

	return (
		<>
			{ 'active' !== value.status && (
				<div className="nab-content-panel__content__missing-element-warning">
					<Dashicon icon="warning" />
					<span>
						{ _x(
							'This element was not found on the current page',
							'text',
							'nelio-ab-testing'
						) }
					</span>
				</div>
			) }
			<BaseControl
				id="nab-content-editor"
				label={ _x( 'Content', 'text', 'nelio-ab-testing' ) }
			>
				<Editor
					className={ clsx( classes ) }
					value={ value.html || value.originalHtml }
					onTextChange={ ( e ) => setHtml( e.htmlValue || '' ) }
					headerTemplate={ customHeader }
					style={ { height: '160px' } }
					formats={ [ ...formats ] }
				/>
			</BaseControl>
		</>
	);
};

// =====
// HOOKS
// =====

type ElementValue = Extract< CssWorkingContentValue, { type: 'element' } >;
const useValue = ( selector: string ): Maybe< ElementValue > => {
	const [ values ] = useContentValues();
	return values.find(
		( v ): v is ElementValue =>
			'element' === v.type && v.selector === selector
	);
};

const FORMATS = [ 'bold', 'italic', 'underline', 'link' ] as const;
const useFormats = ( value: Maybe< ElementValue > ) =>
	FORMATS.filter( ( f ) => ! value?.formats[ f ] );

const useHtmlSetter = ( value: Maybe< ElementValue > ) => {
	const [ _, setContentValue ] = useContentValues();
	return ( html: string ) =>
		value &&
		setContentValue( value.selector, setProperHtmlTags( value, html ) );
};

const useContentValues = (): [
	ReadonlyArray< CssWorkingContentValue >,
	( selector: string, html: string ) => void,
] => {
	const editor = useSelect(
		( select ) =>
			select( NAB_DATA ).getPageAttribute( 'css-editor/cssEditorState' ),
		[]
	);
	const { setPageAttribute } = useDispatch( NAB_DATA );

	if ( editor?.mode !== 'content-editor' ) {
		return [ [], () => void null ];
	}

	const contentValues = editor.contentValues;
	return [
		contentValues,
		( selector, html ) => {
			void setPageAttribute( 'css-editor/cssEditorState', {
				...editor,
				contentValues: contentValues.map( ( cv ) =>
					'element' === cv.type && cv.selector === selector
						? {
								...cv,
								wasJustCreated: undefined,
								html,
						  }
						: cv
				),
			} );
		},
	];
};

// =======
// HELPERS
// =======

const AUX = document.createElement( 'div' );
function setProperHtmlTags(
	value: Extract< CssWorkingContentValue, { type: 'element' } >,
	html: string
): string {
	AUX.innerHTML = html;
	const paragraphs = [ ...AUX.querySelectorAll( 'p' ) ];

	const tagName = getTagName( value.selector );
	if ( tagName === 'ol' || tagName === 'ul' ) {
		return paragraphs
			.filter( ( p ) => !! p.innerHTML.trim() )
			.map( ( p ) => `<li>${ p.innerHTML }</li>` )
			.join( '' );
	}

	return paragraphs
		.map( ( p ) => p.innerHTML )
		.map( ( p ) => ( p.trim() === '<br>' ? '' : p ) )
		.join( '<br>' );
}

const getTagName = ( selector: string ) =>
	last( selector.split( ' ' ) )?.split( /[#.:]/ )[ 0 ] || '';

// =======================
// HACK: Translate buttons
// =======================

let didAlreadyRun = false;
if ( ! didAlreadyRun ) {
	didAlreadyRun = true;
	domReady( () => {
		const style = document.createElement( 'style' );
		const labels = {
			link: _x( 'Link:', 'text', 'nelio-ab-testing' ),
			edit: _x( 'Edit', 'command', 'nelio-ab-testing' ),
			remove: _x( 'Remove', 'command', 'nelio-ab-testing' ),
			save: _x( 'Save', 'command', 'nelio-ab-testing' ),
		};

		style.innerHTML = `
			#nab-css-editor .nab-content-panel__content .ql-tooltip::before {
				content: ${ JSON.stringify( labels.link ) } !important;
			}

			#nab-css-editor .nab-content-panel__content .ql-tooltip .ql-action::after {
				content: ${ JSON.stringify( labels.edit ) } !important;
			}

			#nab-css-editor .nab-content-panel__content .ql-tooltip .ql-remove::before {
				content: ${ JSON.stringify( labels.remove ) } !important;
			}

			#nab-css-editor .nab-content-panel__content .ql-tooltip[data-mode="link"] &::before {
				content: ${ JSON.stringify( labels.link ) } !important;
			}

			#nab-css-editor .nab-content-panel__content .ql-tooltip[data-mode="link"] .ql-action::after {
				content: ${ JSON.stringify( labels.save ) } !important;
			}
		`;
		document.head.appendChild( style );
	} );
}
